/**
 * What the Director's SCENE card and its ON PLAYER SCREENS NOW strip say about the scene on the
 * table: its kind, its name, a one-line detail (the round and who is still standing on each side),
 * and whether the fight is live or over. Pure, so the copy is unit-tested without a render harness;
 * `GmDirectivePanel` feeds it the table scene, the encounter list and the live encounter state.
 */
import { isAble, isActor, sideOf, type EncounterState, type TableScene } from "@lancer-vtt/domain";

/** The scene's fight, read off the live encounter: the round and the able actors on each side. */
export type FightStatus = { round: number; lancers: number; hostiles: number; over: boolean };

/** The round and side counts of a live encounter, and whether its fight is over. */
export function fightStatusOf(state: EncounterState, over: boolean): FightStatus {
  let lancers = 0;
  let hostiles = 0;
  for (const c of Object.values(state.combatants)) {
    if (!isActor(c) || !isAble(c)) continue;
    if (sideOf(c) === "pc") lancers += 1;
    else hostiles += 1;
  }
  return { round: state.activation.round, lancers, hostiles, over };
}

export type SceneCard = {
  /** `COMBAT SCENE` or `NARRATIVE SCENE`. */
  kind: string;
  name: string;
  /** The round and sides (`ROUND 2 · 3 LANCERS VS 4 HOSTILES`), or what a narrative table shows. */
  detail: string;
  /** `LIVE`, `OVER`, or `ON THE TABLE` for narrative. */
  state: "LIVE" | "OVER" | "ON THE TABLE";
  /** The strip's one-liner: the name plus `· combat live` / `· combat over`. */
  line: string;
  isCombat: boolean;
};

/**
 * The SCENE card for the table's scene. `fight` is null while the encounter's state is still
 * loading (the detail reads `LINKING…`), and is ignored for a narrative scene.
 */
export function sceneCard(args: { scene: TableScene; name: string; fight: FightStatus | null }): SceneCard {
  const { scene, name, fight } = args;
  if (scene.kind === "narrative") {
    return { kind: "NARRATIVE SCENE", name, detail: "No board on screen", state: "ON THE TABLE", line: name, isCombat: false };
  }
  const over = fight?.over ?? false;
  const sides = fight === null ? "LINKING…" : `ROUND ${fight.round} · ${fight.lancers} ${fight.lancers === 1 ? "LANCER" : "LANCERS"} VS ${fight.hostiles} ${fight.hostiles === 1 ? "HOSTILE" : "HOSTILES"}`;
  return {
    kind: "COMBAT SCENE",
    name,
    detail: over ? `COMBAT OVER · ${sides}` : sides,
    state: over ? "OVER" : "LIVE",
    line: `${name} · ${over ? "combat over" : "combat live"}`,
    isCombat: true,
  };
}

/**
 * Whether an encounter's fight is over with its wear still off the roster: the GM ended it and no
 * commit is recorded for it (`RosterState.encounterCommits`). The Director offers COMMIT WEAR and
 * ENCOUNTERS tags the row while this holds; `roster` null (still loading) reads as nothing owed.
 */
export function wearUncommitted(args: { encounter: { id: string; combatEndedAt?: string }; roster: { encounterCommits?: Record<string, string> } | null }): boolean {
  const { encounter, roster } = args;
  return encounter.combatEndedAt !== undefined && roster !== null && roster.encounterCommits?.[encounter.id] === undefined;
}
